import { Injectable, Injector } from '@angular/core';
import { Building } from './building.interface';
import { ProjectService } from '../project/project.service';
import { ApiService } from '../../api/api.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { BuildingCoreService } from './building-core.service';
import { ModalService } from '../../services/modal/modal.service';
import { FloorAreaService } from '../../services/floor-area/floor-area.service';
import { Observable } from 'rxjs';

/**
 * Building service.
 *
 * The public interface for the BuildingCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class BuildingService extends BuildingCoreService {

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    private floorAreaService: FloorAreaService,
    protected projectService: ProjectService,
    private modalService: ModalService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, projectService);
  }

  /**
   * Delete building.
   *
   * @note When deleting with a confirm modal, it is not needed to subscribe.
   *
   * @param building The building to delete.
   * @param [confirm=false] Whether to show confirm modal before deletion.
   * @returns Observable with boolean upon deletion. Can be omitted when deleting by modal.
   */
  public delete(building: Building, confirm: boolean = false): Observable<boolean> {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('areaDeleteTitle'),
        content: this.translateService.get('areyousuredeletingarea'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(building, false).subscribe();
        }
      });
    } else {
      return new Observable(observer => {
        this._delete(building).subscribe(() => {
          this.toastService.open(this.translateService.get('areaDeleted'));
          observer.next(true);
          observer.complete();
        });
      });
    }
  }

  /**
   * Get total FNO for a building.
   *
   * @param building The building to get the Total Fno for.
   * @returns Total building FNO.
   */
  public getFno(building: Building): number {
    return this.floorAreaService.getBuildingFNO(building);
  }

  /**
   * Get quality level for the primary building.
   *
   * Currently auto selects the first building in the project to get the
   * quality level from.
   *
   * @returns The primary building's quality level.
   */
  public getQualityLevel(): Building['qualityLevel'] {
    return this.get()[0].qualityLevel;
  }

  /**
   * Patch multiple building values.
   *
   * Use only when the other methods are insufficient.
   *
   * @param building The building to patch.
   * @param values The new values to set.
   */
  public patch(building: Building, values: Partial<Building>): void {
    this._patchBuilding(building, values).subscribe(results => {
      console.log(results);
      this.toastService.open(this.translateService.get('buildingUpdated'));
    });
  }

  /**
   * Set the ratio of the building.
   *
   * @todo `cluster.buildingId = building._id;` should not be needed!
   *
   * @param building The building to set the ratio for.
   * @param ratio The updated ratio value.
   */
  public setRatio(building: Building, ratio: Building['ratio']): void {
    this._patchBuilding(building, { ratio: ratio, ratioBy: 'building' }).subscribe(() => {
      building.clusters.filter(cluster => cluster.ratio !== ratio).forEach(cluster => {
        cluster.buildingId = building._id;
        // I don't remember why this distinction was added, so it's not deleted for now.
        // Should be checked again on ratio/fno/bvo refactor (SPEC-2727).
        // const clusterRatio = this.floorAreaService.getClusterFNO(cluster) > 0
        //   ? ratio
        //   : 1;
        const clusterRatio = ratio;
        this.apiService.updateCluster(cluster, { ratio: clusterRatio }).subscribe(() => {
          this.projectService.patchCluster(cluster, { ratio: clusterRatio });
        });
      });
      this.toastService.open(this.translateService.get('buildingUpdated'));
    });
  }

  /**
   * Toggle the way the BVO is calculated.
   *
   * Options:
   *  - `ratio`: Use BVO/FNO ratio to calculate the BVO.
   *  - `bvo`: Use BVO value to calculate the BVO/FNO ratio for.
   *
   * @param building The building to change ratio for.
   * @param ratioAs Track ratio as.
   */
  public setRatioAs(building: Building, ratioAs: Building['ratioAs']): void {
    this._patchBuilding(building, { ratioAs: ratioAs }).subscribe(() => {
      this.toastService.open(this.translateService.get('buildingUpdated'));
    });
  }

  /**
   * Set the entity to track the ratios for.
   *
   * Options:
   *  - `building`: Set BVO/FNO for the whole building, and reset all cluster ratios to 1/1.
   *  - `cluster`: Set BVO/FNO per cluster, and reset building ratio to 1/1.
   *
   * @param building The building to change ratio for.
   * @param ratioAs Track ratio by.
   * @param [modal=true] Show modal before patching.
   */
  public setRatioBy(building: Building, ratioBy: Building['ratioBy'], modal: boolean = true): void {
    if (!modal) {
      if (ratioBy === 'building') {
        this.setRatio(building, building.ratio);
      } else if (ratioBy === 'cluster') {
        this._patchBuilding(building, { ratioBy: ratioBy }).subscribe(() => {
          this.toastService.open(this.translateService.get('buildingUpdated'));
        });
      }
      return;
    }

    this.modalService.confirm({
      title: `${ this.translateService.get('setRatioBy') } ${ this.translateService.get(ratioBy).toLowerCase() }`,
      content: this.translateService.get('setRatioAsWarning'),
      actions: [{ label: this.translateService.get('submit'), action: 'submit', icon: 'check' }]
    }).afterClosed().subscribe(action => {
      if (action === 'submit') {
        this.setRatioBy(building, ratioBy, false);
      }
    });
  }
}
